import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Scale } from "lucide-react";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function BMICalculator() {
  const [weight, setWeight] = useState("");
  const [height, setHeight] = useState("");
  const [unit, setUnit] = useState("metric");
  const [bmi, setBmi] = useState<number | null>(null);
  const [category, setCategory] = useState("");

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Health", url: "https://calclab.app/health" },
      { name: "BMI Calculator", url: "https://calclab.app/calculator/bmi" }
    ], lang),
    (lang) => calculatorToolSchema(
      "BMI Calculator",
      "Calculate your Body Mass Index (BMI) using metric or imperial units. Free online BMI calculator with instant results.",
      "https://calclab.app/calculator/bmi",
      lang
    )
  ]);

  const calculateBMI = () => {
    const w = parseFloat(weight);
    const h = parseFloat(height);
    
    if (!w || !h || w <= 0 || h <= 0) return;
    
    let bmiValue: number;
    if (unit === "metric") {
      bmiValue = w / (h * h);
    } else {
      bmiValue = (w / (h * h)) * 703;
    }
    
    setBmi(parseFloat(bmiValue.toFixed(1)));
    
    if (bmiValue < 18.5) setCategory("Underweight");
    else if (bmiValue < 25) setCategory("Normal weight");
    else if (bmiValue < 30) setCategory("Overweight");
    else setCategory("Obese");
  };

  return (
    <PageLayout 
      calculatorName="BMI Calculator" 
      calculatorPath="/calculator/bmi"
      category="general"
      subcategory="Health"
    >
      <div className="grid gap-8 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Calculate BMI</CardTitle>
            <CardDescription>Enter your measurements</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Unit System</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="metric">Metric (kg, m)</SelectItem>
                  <SelectItem value="imperial">Imperial (lbs, in)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Weight ({unit === "metric" ? "kg" : "lbs"})</Label>
              <Input
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder="70"
              />
            </div>
            <div>
              <Label>Height ({unit === "metric" ? "m" : "inches"})</Label>
              <Input
                type="number"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                placeholder={unit === "metric" ? "1.75" : "69"}
                step={unit === "metric" ? "0.01" : "1"}
              />
            </div>
            <Button onClick={calculateBMI} className="w-full">
              Calculate BMI
            </Button>
          </CardContent>
        </Card>

        {bmi !== null && (
          <>
            <Card>
              <CardHeader>
                <CardTitle>Your BMI</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <div className="text-5xl font-bold text-primary mb-2">{bmi}</div>
                  <div className="text-xl text-muted-foreground">{category}</div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Underweight:</span>
                    <span className="text-muted-foreground">&lt; 18.5</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Normal weight:</span>
                    <span className="text-muted-foreground">18.5 - 24.9</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Overweight:</span>
                    <span className="text-muted-foreground">25 - 29.9</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Obese:</span>
                    <span className="text-muted-foreground">≥ 30</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="md:col-span-2">
              <CalculatorSuperCalcCTA config={calculatorCTAs["bmi"]} trigger={bmi} />
            </div>
          </>
        )}

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>About BMI</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Body Mass Index (BMI) is a measure of body fat based on height and weight. It's calculated as weight (kg) divided by height squared (m²).
              While BMI is a useful screening tool, it doesn't directly measure body fat and may not be accurate for athletes or those with high muscle mass.
            </p>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
